import React, { useMemo, useState, useCallback } from 'react';
import { Tree, Input, Tag, Empty, Typography } from 'antd';
import { CloseOutlined, GlobalOutlined, FolderOutlined, AppstoreOutlined, TagOutlined } from '@ant-design/icons';
import type { DataNode } from 'antd/lib/tree';

import { LifeSphere, OfferBinding, OfferBindingScope } from '../types';

interface Props {
  spheres: LifeSphere[];
  productId: string;
  productName: string;
  value: OfferBinding[];
  onChange: (next: OfferBinding[]) => void;
  treeHeight?: number;
}

const PREFIX: Record<OfferBindingScope, string> = {
  product: 'p',
  sphere: 's',
  theme: 't',
  subtheme: 'st',
};

const keyOf = (scope: OfferBindingScope, id: string) => `${PREFIX[scope]}:${id}`;
const keyOfBinding = (b: OfferBinding) => keyOf(b.scope, b.scopeId);

const parseKey = (k: string): OfferBinding => {
  const [prefix, id] = k.split(':');
  const scope: OfferBindingScope =
    prefix === 'p' ? 'product' : prefix === 's' ? 'sphere' : prefix === 't' ? 'theme' : 'subtheme';
  return { scope, scopeId: id };
};

const scopeIcon = (scope: OfferBindingScope) => {
  if (scope === 'product') return <GlobalOutlined />;
  if (scope === 'sphere') return <FolderOutlined />;
  if (scope === 'theme') return <AppstoreOutlined />;
  return <TagOutlined />;
};

const scopeColor = (scope: OfferBindingScope): string => {
  if (scope === 'product') return 'magenta';
  if (scope === 'sphere') return 'blue';
  if (scope === 'theme') return 'cyan';
  return 'geekblue';
};

const OfferBindingTree: React.FC<Props> = ({ spheres, productId, productName, value, onChange, treeHeight = 380 }) => {
  const [search, setSearch] = useState('');
  const defaultExpanded = useMemo(() => {
    const keys: React.Key[] = [keyOf('product', productId)];
    spheres.forEach((s) => {
      keys.push(keyOf('sphere', s.id));
      s.themes.forEach((c) => keys.push(keyOf('theme', c.id)));
    });
    return keys;
  }, [spheres, productId]);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>(defaultExpanded);
  const [autoExpandParent, setAutoExpandParent] = useState(true);

  // Build lookup maps
  const { pathByKey, allKeys, treeData } = useMemo(() => {
    const pathByKey: Record<string, string> = {};
    const allKeys: string[] = [];

    const prodKey = keyOf('product', productId);
    pathByKey[prodKey] = `Весь продукт: ${productName}`;
    allKeys.push(prodKey);

    const tree: DataNode[] = [{
      key: prodKey,
      title: (
        <span>
          <GlobalOutlined style={{ marginRight: 6, color: '#eb2f96' }} />
          Весь продукт: <b>{productName}</b>
        </span>
      ),
      children: spheres.map((s) => {
        const sKey = keyOf('sphere', s.id);
        pathByKey[sKey] = s.name;
        allKeys.push(sKey);
        return {
          key: sKey,
          title: (
            <span>
              <FolderOutlined style={{ marginRight: 6, color: '#1890ff' }} />
              {s.name}
            </span>
          ),
          children: s.themes.map((c) => {
            const cKey = keyOf('theme', c.id);
            pathByKey[cKey] = `${s.name} / ${c.name}`;
            allKeys.push(cKey);
            return {
              key: cKey,
              title: (
                <span>
                  <AppstoreOutlined style={{ marginRight: 6, color: '#13c2c2' }} />
                  {c.name}
                </span>
              ),
              children: c.subthemes.map((t) => {
                const tKey = keyOf('subtheme', t.id);
                pathByKey[tKey] = `${s.name} / ${c.name} / ${t.name}`;
                allKeys.push(tKey);
                return {
                  key: tKey,
                  title: (
                    <span>
                      <TagOutlined style={{ marginRight: 6, color: '#2f54eb' }} />
                      {t.name}
                    </span>
                  ),
                };
              }),
            };
          }),
        };
      }),
    }];
    return { pathByKey, allKeys, treeData: tree };
  }, [spheres, productId, productName]);

  const expandBindingToAllDescendants = useCallback((b: OfferBinding): string[] => {
    const out: string[] = [];
    if (b.scope === 'product') {
      return allKeys;
    }
    if (b.scope === 'sphere') {
      const s = spheres.find((x) => x.id === b.scopeId);
      if (!s) return [];
      out.push(keyOf('sphere', s.id));
      s.themes.forEach((c) => {
        out.push(keyOf('theme', c.id));
        c.subthemes.forEach((t) => out.push(keyOf('subtheme', t.id)));
      });
      return out;
    }
    if (b.scope === 'theme') {
      let found: { c: any; s: any } | null = null;
      spheres.forEach((s) => s.themes.forEach((c) => { if (c.id === b.scopeId) found = { c, s }; }));
      if (!found) return [];
      out.push(keyOf('theme', found.c.id));
      found.c.subthemes.forEach((t: any) => out.push(keyOf('subtheme', t.id)));
      return out;
    }
    return [keyOf('subtheme', b.scopeId)];
  }, [spheres, allKeys]);

  const checkedKeys = useMemo(() => {
    const set = new Set<string>();
    value.forEach((b) => expandBindingToAllDescendants(b).forEach((k) => set.add(k)));
    return Array.from(set);
  }, [value, expandBindingToAllDescendants]);

  const normalize = useCallback((checked: string[]): OfferBinding[] => {
    const set = new Set(checked);
    const prodKey = keyOf('product', productId);
    if (set.has(prodKey)) return [{ scope: 'product', scopeId: productId }];

    const out: OfferBinding[] = [];
    spheres.forEach((s) => {
      if (set.has(keyOf('sphere', s.id))) {
        out.push({ scope: 'sphere', scopeId: s.id });
        return;
      }
      s.themes.forEach((c) => {
        if (set.has(keyOf('theme', c.id))) {
          out.push({ scope: 'theme', scopeId: c.id });
          return;
        }
        c.subthemes.forEach((t) => {
          if (set.has(keyOf('subtheme', t.id))) {
            out.push({ scope: 'subtheme', scopeId: t.id });
          }
        });
      });
    });
    return out;
  }, [spheres, productId]);

  const handleCheck = (checked: any) => {
    const keys: string[] = Array.isArray(checked) ? checked : checked.checked;
    onChange(normalize(keys));
  };

  const handleSearch = (q: string) => {
    setSearch(q);
    if (!q) {
      setExpandedKeys([]);
      setAutoExpandParent(false);
      return;
    }
    const lower = q.toLowerCase();
    const toExpand = new Set<React.Key>();
    const visit = (nodes: DataNode[], ancestors: React.Key[]) => {
      nodes.forEach((n) => {
        const title = pathByKey[n.key as string] || '';
        if (title.toLowerCase().includes(lower)) {
          ancestors.forEach((a) => toExpand.add(a));
        }
        if (n.children) visit(n.children, [...ancestors, n.key]);
      });
    };
    visit(treeData, []);
    setExpandedKeys(Array.from(toExpand));
    setAutoExpandParent(true);
  };

  const filterTreeNode = (node: any) => {
    if (!search) return false;
    const title = pathByKey[node.key as string] || '';
    return title.toLowerCase().includes(search.toLowerCase());
  };

  const removeBinding = (b: OfferBinding) => {
    onChange(value.filter((x) => !(x.scope === b.scope && x.scopeId === b.scopeId)));
  };

  const isProductChecked = value.some((b) => b.scope === 'product');

  return (
    <div>
      <Input.Search
        placeholder="Поиск по сферам, темам, подтемам…"
        allowClear
        onChange={(e) => handleSearch(e.target.value)}
        style={{ marginBottom: 8 }}
      />

      <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 8, marginBottom: 12 }}>
        <Tree
          checkable
          treeData={treeData}
          checkedKeys={checkedKeys}
          onCheck={handleCheck}
          expandedKeys={expandedKeys}
          onExpand={(keys) => { setExpandedKeys(keys); setAutoExpandParent(false); }}
          autoExpandParent={autoExpandParent}
          filterTreeNode={filterTreeNode}
          height={treeHeight}
          selectable={false}
        />
      </div>

      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        Выбрано привязок: {value.length}
        {isProductChecked && ' (оффер применяется ко всему продукту)'}
      </Typography.Text>

      {value.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {value.map((b) => {
            const key = keyOfBinding(b);
            const path = pathByKey[key] || '—';
            return (
              <Tag
                key={key}
                color={scopeColor(b.scope)}
                closable
                closeIcon={<CloseOutlined />}
                onClose={(e) => { e.preventDefault(); removeBinding(b); }}
                style={{ padding: '2px 8px', fontSize: 12 }}
              >
                <span style={{ marginRight: 4 }}>{scopeIcon(b.scope)}</span>
                {path}
              </Tag>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OfferBindingTree;
